import React, {
	useEffect,
	ReactElement,
	useState,
	useMemo,
	useCallback,
} from 'react'
import {
	IOptionValue,
	FilterDetail,
	IFlattenedAdornment,
	SearchBar,
	ISearchBarLocales,
} from '@copyrightagent/basic-components'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCurrentListTypeInUrl } from 'lib/utils'
import { EListType, STATE_TO_TRANSLATIONKEY, ICaseState } from 'lib/entities/Case'
import { Filters, getDefaultFilters } from 'components/CaseListView'
import getCountryByCode from 'lib/getCountryByCode'

interface IProps {
	initialFilters: Filters
}

export default function CaseSearchAndFilter(props: IProps): ReactElement {
	const { initialFilters } = props
	const { t } = useTranslation()
	const listType = getCurrentListTypeInUrl()
	const history = useHistory()
	const location = useLocation()
	const [caseStateOptions, setCaseStateOptions] = useState<IOptionValue[]>([])
	const [countryOptions, setCountryOptions] = useState<IOptionValue[]>([])
	const [caseTypeOptions, setSetCaseTypeOptions] = useState<IOptionValue[]>([])

	useEffect(() => {
		const countryValueAndLabels = initialFilters.countries
			.filter((country) => country !== 'unknown' && country !== '-')
			.map((country) => ({
				value: country,
				label: getCountryByCode(country),
			}))
		setCountryOptions(countryValueAndLabels)
	}, [initialFilters.countries, t])

	useEffect(() => {
		const caseTypeValueAndLabels = initialFilters.caseTypes.map((caseType) => ({
			value: caseType,
			label: t(`filter/caseTypes/${caseType}`),
		}))
		setSetCaseTypeOptions(caseTypeValueAndLabels)
	}, [initialFilters.caseTypes, t])

	useEffect(() => {
		const uniqueStates = new Map<string, string>()

		initialFilters.caseState.forEach((state) => {
			const translationKey = STATE_TO_TRANSLATIONKEY[state as ICaseState]
			if (!uniqueStates.has(translationKey)) {
				uniqueStates.set(translationKey, state)
			}
		})

		const options = Array.from(uniqueStates.entries()).map(([translationKey, state]) => ({
			value: state,
			label: t(translationKey),
		}))

		setCaseStateOptions(options)
	}, [initialFilters.caseState, t])

	const getFilterDetails = useCallback(
		(currentFilters: Filters): FilterDetail<Filters>[] => [
			{
				shouldRender: listType === EListType.all,
				filterKey: 'caseState',
				locales: {
					label: t('filterCases/caseStateLabel'),
					noOptionsAvailable: t('filterCases/noCaseStateAvailable'),
					placeholder: t('filterCases/caseState'),
				},
				filterOptions: caseStateOptions,
				selectedValue: currentFilters.caseState,
			},
			{
				filterKey: 'caseTypes',
				locales: {
					label: t('filterCases/caseTypeLabel'),
					noOptionsAvailable: t('filterCases/noCaseTypesAvailable'),
					placeholder: t('filterCases/caseTypes'),
				},
				filterOptions: caseTypeOptions,
				selectedValue: currentFilters.caseTypes,
			},
			{
				filterKey: 'countries',
				locales: {
					label: t('filterCases/caseCountryLabel'),
					noOptionsAvailable: t('filterCases/noCountriesAvailable'),
					placeholder: t('filterCases/caseCountries'),
				},
				filterOptions: countryOptions,
				selectedValue: currentFilters.countries,
			},
		],
		[t, caseStateOptions, listType, caseTypeOptions, countryOptions],
	)

	const setLabelByKey = useCallback(
		(
			key: keyof Filters,
			adornment: IFlattenedAdornment<Filters>,
		): IFlattenedAdornment<Filters> => {
			if (key === 'countries')
				adornment.label = getCountryByCode(adornment.value)
			if (key === 'caseTypes')
				adornment.label = t(`filter/caseTypes/${adornment.value}`)
			if (key === 'caseState') {
				const translationKey = STATE_TO_TRANSLATIONKEY[adornment.value as ICaseState]
				adornment.label = t(translationKey)
			}

			return adornment
		},
		[t],
	)

	const locales: ISearchBarLocales = useMemo(
		() => ({
			filterSearchLocales: {
				submitText: t('searchCases/submitText'),
				placeholder: t('searchCases/textFieldPlaceholder'),
			},
			filterPopoverLocales: {
				reset: t('filterCases/reset'),
				apply: t('filterCases/apply'),
			},
		}),
		[t],
	)

	return (
		<SearchBar<Filters>
			getDefaultFilters={getDefaultFilters}
			setLabelByKey={setLabelByKey}
			getFilterDetails={getFilterDetails}
			locales={locales}
			location={location}
			history={history}
			onSearchSubmit={() => undefined}
		/>
	)
}
